import React from 'react'
import classNames from 'classnames'
import {useDispatch} from 'react-redux'
import {WindowContent} from './WindowContent'
import {acArrowExpand, acChevron2Down, acChevron2Top} from 'ac-icon'
import {Loading} from 'ac_ui'
import {IWindow, WindowEnum} from '../../domain/IWindow'
import {ComponentEnum, ComponentNameEnum} from '../../componentsRegistry/Registry'
import {taskActions} from '../../redux/actions'
import {ToolbarTab} from '../../toolbar/ToolbarTab'
import {ToolbarGroupTab} from '../../toolbar/ToolbarGroupTab'
import {Toolbar} from '../../toolbar/Toolbar'
import {ToolbarButtonImg} from '../../toolbar/ToolbarButtonImg'
import {ExecuteButton} from '../../ExecuteButton'
import {getTitleSplit} from '../../utils'

interface ITaskEditorWindowProps {
  type: WindowEnum
  windowProp: IWindow

  isExecuting?: boolean
  handleExecute?: () => Promise<void>
  splitTabs: (
    leftComponentId: ComponentEnum,
    rightComponentId: ComponentEnum,
    fullscreenActiveLeftComponentId: ComponentEnum,
    fullscreenActiveRightComponentId: ComponentEnum,
  ) => void
}

const TaskEditorWindow: React.FC<ITaskEditorWindowProps> = ({
  type,
  windowProp,
  isExecuting,
  handleExecute,
  splitTabs,
}): React.ReactElement => {
  const taskDispatch = useDispatch()

  let content: React.ReactElement
  const {activeComponentId, tab, isCollapsed} = windowProp
  const isEmptyWindowContent = !tab.first && !tab.second && !tab.third
  const isBottom = type === WindowEnum.bottomLeftWindow || type === WindowEnum.bottomRightWindow

  let isSplit = false
  let splitLeftComponentId: ComponentEnum
  let splitRightComponentId: ComponentEnum
  let fullscreenActiveLeftComponentId: ComponentEnum
  let fullscreenActiveRightComponentId: ComponentEnum

  let innerActiveComponentId: ComponentEnum
  let activeTab: 'first' | 'second' | 'third'

  const onChangeCollapse = (): void => {
    taskDispatch(taskActions.setCollapsedInWindow(type, !isCollapsed))
  }

  const onClickToolbarTab = (currentId: ComponentEnum, activeId: ComponentEnum): void => {
    if (currentId !== activeId) {
      taskDispatch(taskActions.setActiveInWindow(type, currentId, isBottom))
    } else if (isBottom && isCollapsed) {
      onChangeCollapse()
    }
  }

  const onClickInnerToolbarTab = (
    currentId: ComponentEnum,
    activeId: ComponentEnum,
    tabKey: 'first' | 'second' | 'third',
  ): void => {
    if (currentId !== activeId) {
      taskDispatch(taskActions.setInnerActiveInWindow(type, tabKey, currentId))
    }
  }

  if (isEmptyWindowContent) {
    content = (
      <div className={'flex-grow-1 d-flex flex-column align-items-center justify-content-center'}>
        <div className={'d-flex'}>
          <Loading />
        </div>
      </div>
    )
  } else {
    const isOneTab: boolean =
      (!!tab.first && !tab.second && !tab.third) ||
      (!tab.first && !!tab.second && !tab.third) ||
      (!tab.first && !tab.second && !!tab.third)
    const isTwoTab: boolean =
      (!tab.first && !!tab.second && !!tab.third) ||
      (!!tab.first && !tab.second && !!tab.third) ||
      (!!tab.first && !!tab.second && !tab.third)
    let firstToolbar: React.ReactElement
    let secondToolbar: React.ReactElement
    let thirdToolbar: React.ReactElement

    if (tab.first) {
      const componentId: ComponentEnum = tab.first.componentId
      const componentName = ComponentNameEnum[componentId]
      if (componentId === activeComponentId) {
        activeTab = 'first'
        innerActiveComponentId = tab.first.innerActiveComponentId
      }
      if (isTwoTab) {
        isSplit = true
        splitLeftComponentId = componentId
        if (tab.first.innerActiveComponentId) {
          fullscreenActiveLeftComponentId = tab.first.innerActiveComponentId
        }
      }
      firstToolbar = (
        <ToolbarTab
          name={componentName}
          isActive={componentId === activeComponentId && !isCollapsed}
          onClick={
            isOneTab && !isBottom
              ? undefined
              : () => {
                  onClickToolbarTab(componentId, activeComponentId)
                }
          }
        />
      )
    }
    if (tab.second) {
      const componentId: ComponentEnum = tab.second.componentId
      const componentName = ComponentNameEnum[componentId]
      if (componentId === activeComponentId) {
        activeTab = 'second'
        innerActiveComponentId = tab.second.innerActiveComponentId
      }
      if (isTwoTab) {
        isSplit = true
        if (!splitLeftComponentId) {
          splitLeftComponentId = componentId
          if (tab.second.innerActiveComponentId) {
            fullscreenActiveLeftComponentId = tab.second.innerActiveComponentId
          }
        } else {
          splitRightComponentId = componentId
          if (tab.second.innerActiveComponentId) {
            fullscreenActiveRightComponentId = tab.second.innerActiveComponentId
          }
        }
      }
      secondToolbar = (
        <ToolbarTab
          name={componentName}
          isActive={componentId === activeComponentId && !isCollapsed}
          onClick={
            isOneTab && !isBottom
              ? undefined
              : () => {
                  onClickToolbarTab(componentId, activeComponentId)
                }
          }
        />
      )
    }
    if (tab.third) {
      const componentId: ComponentEnum = tab.third.componentId
      const componentName = ComponentNameEnum[componentId]
      if (componentId === activeComponentId) {
        activeTab = 'third'
        innerActiveComponentId = tab.third.innerActiveComponentId
      }
      if (isTwoTab) {
        isSplit = true
        if (!splitLeftComponentId) {
          splitLeftComponentId = componentId
          if (tab.third.innerActiveComponentId) {
            fullscreenActiveLeftComponentId = tab.third.innerActiveComponentId
          }
        } else {
          splitRightComponentId = componentId
          if (tab.third.innerActiveComponentId) {
            fullscreenActiveRightComponentId = tab.third.innerActiveComponentId
          }
        }
      }
      thirdToolbar = (
        <ToolbarTab
          name={componentName}
          isActive={componentId === activeComponentId && !isCollapsed}
          onClick={
            isOneTab && !isBottom
              ? undefined
              : () => {
                  onClickToolbarTab(componentId, activeComponentId)
                }
          }
        />
      )
    }

    if (isSplit) {
      if (splitLeftComponentId === ComponentEnum.masterResult && splitRightComponentId === ComponentEnum.schema) {
        isSplit = false
      }
    }

    const collapseButtonTitle = isCollapsed ? 'Развернуть' : 'Свернуть'

    content = (
      <>
        <Toolbar>
          <ToolbarGroupTab>
            {firstToolbar}
            {secondToolbar}
            {thirdToolbar}
            {isSplit && (
              <ToolbarButtonImg
                title={getTitleSplit(splitLeftComponentId, splitRightComponentId)}
                onClick={() => {
                  splitTabs(
                    splitLeftComponentId,
                    splitRightComponentId,
                    fullscreenActiveLeftComponentId,
                    fullscreenActiveRightComponentId,
                  )
                }}
                iconPath={acArrowExpand}
              />
            )}
          </ToolbarGroupTab>
          {type === WindowEnum.topRightWindow && (
            <ExecuteButton isNew={true} isExecuting={isExecuting} onClick={handleExecute} />
          )}
          {isBottom && (
            <ToolbarButtonImg
              className={'collapse-button'}
              title={collapseButtonTitle}
              onClick={onChangeCollapse}
              iconPath={isCollapsed ? acChevron2Top : acChevron2Down}
            />
          )}
        </Toolbar>
        <WindowContent
          id={type + activeComponentId}
          componentId={activeComponentId}
          innerActiveComponentId={innerActiveComponentId}
          onClickInnerToolbarTab={(currentId: ComponentEnum) => {
            onClickInnerToolbarTab(currentId, innerActiveComponentId, activeTab)
          }}
          isScrollContainer={activeComponentId === ComponentEnum.schemaDiagram}
          splitTabs={splitTabs}
        />
      </>
    )
  }
  const collapsedStyle = isCollapsed ? ' collapsed' : ''

  const css = classNames('task-editor-window', isBottom ? 'bottom' : '', collapsedStyle)

  return <div className={css}>{content}</div>
}

export {TaskEditorWindow}
